import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotificationStore } from '../../store/notificationStore';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

export const NotificationBell: React.FC = () => {
  const { notifications, unreadCount, fetchNotifications, markAsRead, subscribeWebPush, clearAllNotifications } = useNotificationStore();
  const [isOpen, setIsOpen] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    
    // Check if web push is supported and not already granted
    if ('Notification' in window && Notification.permission === 'default') {
      setShowPrompt(true);
    }
  }, [fetchNotifications]);

  const handleEnablePush = async () => {
    const success = await subscribeWebPush();
    if (success) setShowPrompt(false);
  };

  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-black/5 text-textSecondary transition-colors focus:outline-none"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        
        {unreadCount > 0 && (
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute top-1 right-1 w-4 h-4 bg-error text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-surface"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.div>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute right-0 mt-2 w-80 bg-surface border border-border rounded-2xl shadow-2xl overflow-hidden z-50 backdrop-blur-xl"
          >
            <div className="p-4 border-b border-border bg-black/5 flex justify-between items-center">
              <h3 className="font-bold text-textTertiary">Notifications</h3>
              <div className="flex gap-3">
                {unreadCount > 0 && (
                  <span className="text-xs text-accent font-medium cursor-pointer hover:underline" onClick={() => notifications.forEach(n => !n.read && markAsRead(n.id))}>
                    Mark all read
                  </span>
                )}
                {notifications.length > 0 && (
                  <span className="text-xs text-textSecondary font-medium cursor-pointer hover:text-error hover:underline" onClick={async () => {
                    await clearAllNotifications();
                  }}>
                    Clear All
                  </span>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="ml-2 text-textSecondary hover:text-error transition-colors focus:outline-none"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {showPrompt && (
              <div className="p-4 bg-accent/10 border-b border-accent/20">
                <p className="text-xs text-text mb-2">Enable desktop notifications so you never miss an update!</p>
                <button 
                  onClick={handleEnablePush}
                  className="text-xs font-bold bg-accent text-white px-3 py-1.5 rounded-lg hover:bg-accent/90 w-full"
                >
                  Enable Notifications
                </button>
              </div>
            )}

            <div className="max-h-[400px] overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-textSecondary text-sm">
                  You're all caught up!
                </div>
              ) : (
                notifications.map((notif) => (
                  <div 
                    key={notif.id}
                    onClick={() => {
                      if (!notif.read) markAsRead(notif.id);
                      setIsOpen(false);
                      if (notif.type === 'MESSAGE' && notif.sourceUsername) {
                        navigate(`/chat/${notif.sourceUsername}`);
                      } else if (notif.ideaId) {
                        navigate(`/view/${notif.ideaId}`);
                      }
                    }}
                    className={`p-4 border-b border-border hover:bg-black/5 cursor-pointer transition-colors ${!notif.read ? 'bg-accent/5' : ''}`}
                  >
                    <div className="flex gap-3">
                      <div className="w-2 h-2 mt-1.5 rounded-full bg-accent shrink-0" style={{ opacity: notif.read ? 0 : 1 }} />
                      <div>
                        <p className="text-sm text-textTertiary">
                          <span className="font-bold">@{notif.sourceUsername || 'Someone'}</span> 
                          {notif.type === 'LIKE' ? ' liked your idea.' :
                           notif.type === 'COMMENT' ? ' commented on your idea.' :
                           notif.type === 'MESSAGE' ? ' sent you a message.' :
                           notif.type === 'POST_IDEA' ? ' posted a new idea.' :
                           notif.type === 'INTEREST' ? ' is interested in your idea.' :
                           ` ${notif.type.toLowerCase()}d your idea.`}
                        </p>
                        <p className="text-xs text-textSecondary mt-1">
                          {new Date(notif.timestamp).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
